import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Switch,
  CircularProgress,
} from "@mui/material";
import { CellTower, PowerSettingsNew, ErrorOutline, DataUsage, SignalCellular4Bar, SignalCellularOff } from "@mui/icons-material";
import { margin, styled } from "@mui/system";
import SectionHeader from "./SectionHeader";
import { useAuth } from "../../../AuthContext";

const StatusContainer = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  gap: "100px",
  width: "100%",
});

const StatusBox = styled(Paper)(({ theme }) => ({
  flex: 1,
  borderRadius: theme.shape.borderRadius * 2,
  overflow: "hidden",
  boxShadow: theme.shadows[3],
}));

const StatusContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: "center",
}));

const StatusValue = styled(Typography)(({ color }) => ({
  fontSize: "16px",
  fontWeight: "bold",
  color: color || "inherit",
  marginTop: "8px",
}));

const PumpToggle = styled(Switch)(({ theme }) => ({
  "& .MuiSwitch-switchBase": {
    "&.Mui-checked": {
      color: theme.palette.success.main,
      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.success.light,
      },
    },
  },
  "& .MuiSwitch-track": {
    backgroundColor: theme.palette.grey[400],
  },
}));

const StatusSection = ({ showSnackbar }) => {
  const {
    data,
    pumpStats,
    isOnline,
    pumpRunningStatus,
    changePumpRunningStatus,
    userRole,
  } = useAuth();
  const canTogglePump = userRole === "admin";
  const [isToggling, setIsToggling] = useState(false);
//console.log('to check for the faults in pump stats',pumpStats)
  const togglePump = async () => {
    if (!isOnline) {
      showSnackbar("Pump is offline", "error");
      return;
    }
    if (isToggling) return;

   // console.log("Toggling pump, current status:", pumpRunningStatus);
    setIsToggling(true);
    const result = await changePumpRunningStatus();
    setIsToggling(false);

    if (result.message) {
      showSnackbar(result.message, result.success ? "success" : "error");
    } else {
      showSnackbar("Unknown error occurred while toggling pump", "error");
    }
  };

  // Determine display status for pump
  const getPumpStatusText = () => {
   // console.log("Current pumpRunningStatus:", pumpRunningStatus);
    if (
      isToggling ||
      pumpRunningStatus === "switching_on" ||
      pumpRunningStatus === "switching_off"
    ) {
      return "SWITCHING...";
    }
    if (pumpRunningStatus === "on") {
      if(isOnline){
return "RUNNING"
      }
      else{
        return "Switching..."
      }
      };
    if (pumpRunningStatus === "off") return "STOPPED";
    return "Switching...";
  };

  const getPumpStatusColor = () => {
    if (
      isToggling ||
      pumpRunningStatus === "switching_on" ||
      pumpRunningStatus === "switching_off"
    ) {
      return "orange";
    }
    if (pumpRunningStatus === "on") return "green";
    if (pumpRunningStatus === "off") return "gray";
    return "gray";
  };

  return (
    <StatusContainer sx={{ marginBottom: "10px" }}>
      {/* Connectivity Status */}
      <StatusBox>
        <SectionHeader icon={<CellTower />} title="Connectivity" />
        <StatusContent>
          {isOnline
            ? <SignalCellular4Bar color="success" fontSize="large" />
            : <SignalCellularOff color="error" fontSize="large" />
          }
          <StatusValue color={isOnline ? "green" : "red"}>
            {isOnline ? "Online" : "Offline"}
          </StatusValue>
          {isOnline && (
            <StatusValue color={isOnline ? "green" : "red"}>
              {`Network Signal: ${data?.network_signal || "0"}`}
            </StatusValue>
          )}
        </StatusContent>
      </StatusBox>
      


 <StatusBox>
        <SectionHeader icon={<DataUsage />} title="Data Availability" />
        <StatusContent>
          <DataUsage
            fontSize="large"
            sx={{ color: data?.network_signal > 20 ? "green" : "orange" }}
          />
          <StatusValue color={data?.network_signal > 20 ? "green" : "orange"}>
            {`Data Availability: ${data?.network_signal ? (data.network_signal * 2.5).toFixed(0) : 30}%`}
          </StatusValue>
        </StatusContent>
      </StatusBox>




      {/* Pump Status */}
      <StatusBox>
        <SectionHeader icon={<PowerSettingsNew />} title="Pump Status" />
        <StatusContent>
          <PowerSettingsNew
            color={
              pumpRunningStatus === true && !isToggling ? "success" : "disabled"
            }
            fontSize="large"
          />
          {canTogglePump && (
            isToggling ? (
              <CircularProgress size={20} sx={{ marginTop: "4px" }} />
            ) : (
              <PumpToggle
                checked={pumpRunningStatus === "on"}
                onChange={togglePump}
                disabled={!isOnline || isToggling}
              />
            )
          )}
          <StatusValue color={getPumpStatusColor()}>
            {getPumpStatusText()}
          </StatusValue>
        </StatusContent>
      </StatusBox>

     {/* Fault Status */}
<StatusBox>
  <SectionHeader icon={<ErrorOutline />} title="Fault Status" />
  <StatusContent style={{ alignItems: 'flex-start' }}>
    <ErrorOutline
      color={pumpStats?.fault?.alert_code ? "error" : "disabled"}
      fontSize="large"
      style={{ marginRight: 8, marginTop: 4 }}
    />
    <StatusValue color={pumpStats?.fault?.alert_code ? "red" : "green"}>
      {pumpStats?.fault?.alert_code ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', rowGap: 2, columnGap: 6 }}>
          <span style={{ fontWeight: 500 }}>Code:</span>
          <span>{pumpStats.fault.alert_code}</span>

          <span style={{ fontWeight: 500 }}>Desc:</span>
          <span>{pumpStats.fault.description}</span>

          <span style={{ fontWeight: 500 }}>Level:</span>
          <span>{pumpStats.fault.alert_level}</span>

          <span style={{ fontWeight: 500 }}>Time:</span>
          <span>
            {new Date(pumpStats.fault.time).toLocaleString('en-IN', {
              timeZone: 'Asia/Kolkata',
              hour12: true,
            })}
          </span>
        </div>
      ) : (
        <span>No Fault</span>
      )}
    </StatusValue>
  </StatusContent>
</StatusBox>
    </StatusContainer>
  );
};

export default StatusSection;
